/**
 * View: Programs Form
 * Multi-step form for creating or editing a program with stepper navigation.
 * Follows the same pattern as TieringSettingsFormView.
 */

import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MainLayout } from "@/components/layout/MainLayout";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Stepper } from "@/components/ui/stepper";
import { Button } from "@/components/ui/button";
import { SuccessModal } from "@/components/ui/success-modal";
import {
  EventTriggerStep,
  IncentiveRuleStep,
  CustomerSegmentStep,
} from "./components";
import {
  type ProgramFormData,
  type ProgramWritePayload,
  type ProgramDetail,
  type TierType,
  type BaseRewardLevel,
  initialProgramFormData,
} from "@/feature/programs/domain/model";
import {
  CreateProgramUseCase,
  UpdateProgramUseCase,
  FetchProgramDetailUseCase,
  UploadProductFileUseCase,
  UploadWhitelistFileUseCase,
} from "@/feature/programs/domain/usecase/ProgramUseCases";

// Re-export types for backward compatibility with step components
export type {
  ProgramFormData,
  ProductIncentiveData,
  CustomerData,
  TierType,
  IncentiveRuleType,
} from "@/feature/programs/domain/model";

const STEPS = [
  { label: "Event Trigger" },
  { label: "Incentive Rule" },
  { label: "Customer Segment" },
];

/**
 * Map API detail response to form data structure.
 * Converts ISO date strings to YYYY-MM-DD format for date inputs.
 */
const mapDetailToFormData = (detail: ProgramDetail): ProgramFormData => {
  // Convert ISO date to YYYY-MM-DD
  const formatDate = (isoDate: string): string => {
    if (!isoDate) return "";
    const date = new Date(isoDate);
    return date.toISOString().split("T")[0];
  };

  const isDuwit = detail.name === "Duwit";

  return {
    // Step 1 - Event Trigger
    programName: detail.programName,
    channel: detail.name,
    startDate: formatDate(detail.startDate),
    endDate: formatDate(detail.endDate),
    // Step 2 - Incentive Rule (non-Duwit)
    incentiveRule: detail.incentiveRule.type === "product" ? "product" : "general",
    basedOn: detail.incentiveRule.basedOn ?? "",
    minTransaction: detail.incentiveRule.minimumTransaction != null ? String(detail.incentiveRule.minimumTransaction) : "",
    maxTransaction: detail.incentiveRule.maximumTransaction != null ? String(detail.incentiveRule.maximumTransaction) : "",
    productIncentiveFile: "",
    productIncentiveList: [],
    productTemporaryFileId: "",
    productTotalCount: detail.incentiveRule.totalProducts ?? 0,
    // Step 2 - Incentive Rule (Duwit-specific)
    baseRewardLevels: isDuwit
      ? (detail.incentiveRule.baseRewards ?? []).map((reward) => ({
          id: crypto.randomUUID(),
          minTransaction: reward.minTransaction,
          maxTransaction: reward.maxTransaction,
          pointsEarned: reward.pointsEarned,
        }))
      : [],
    maxBaseRewardPointsPerDay: detail.incentiveRule.maxBaseRewardPerDay ?? 0,
    stampMinimumTransaction: detail.incentiveRule.stampMultiplier?.minTotalTransaction ?? 0,
    weeklyStampMinDays: detail.incentiveRule.stampMultiplier?.weekly.minActiveDays ?? 0,
    weeklyStampMultiplier: detail.incentiveRule.stampMultiplier?.weekly.multiplierPercentage ?? 0,
    monthlyStampMinDays: detail.incentiveRule.stampMultiplier?.monthly.minActiveDays ?? 0,
    monthlyStampMultiplier: detail.incentiveRule.stampMultiplier?.monthly.multiplierPercentage ?? 0,
    // Step 3 - Customer Segment
    area: detail.customerSegment.areas[0] || "",
    tiers: detail.customerSegment.tiers as TierType[],
    whitelistFile: "",
    customerList: [],
    whitelistTemporaryId: "",
    whitelistTotalCount: detail.customerSegment.whitelistCount,
  };
};

export const ProgramsFormView = (): React.JSX.Element => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEditMode = Boolean(id);
  
  usePageTitle(isEditMode ? "Edit Program" : "Create Program");

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<ProgramFormData>(initialProgramFormData);
  const [errors, setErrors] = useState<
    Partial<Record<keyof ProgramFormData, string>>
  >({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [loadDetailError, setLoadDetailError] = useState<string | null>(null);

  // File upload states
  const [productIncentiveFileError, setProductIncentiveFileError] = useState<string | null>(null);
  const [productFileUploading, setProductFileUploading] = useState(false);
  const [whitelistFileError, setWhitelistFileError] = useState<string | null>(null);
  const [whitelistFileUploading, setWhitelistFileUploading] = useState(false);

  // Load program detail for edit mode
  const loadDetail = useCallback(async (programId: string) => {
    setIsLoadingDetail(true);
    setLoadDetailError(null);
    try {
      const detail = await FetchProgramDetailUseCase(programId);
      setFormData(mapDetailToFormData(detail));
    } catch (error) {
      console.error("Failed to load program detail:", error);
      setLoadDetailError(
        "Failed to load program. The program may not exist or there was a network error."
      );
    } finally {
      setIsLoadingDetail(false);
    }
  }, []);

  useEffect(() => {
    if (isEditMode && id) {
      void loadDetail(id);
    }
  }, [id, isEditMode, loadDetail]);

  const handleInputChange = (field: keyof ProgramFormData, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleTierChange = (tier: TierType, checked: boolean) => {
    setFormData((prev) => {
      const newTiers = checked
        ? [...prev.tiers, tier]
        : prev.tiers.filter((t) => t !== tier);
      return { ...prev, tiers: newTiers };
    });
    if (errors.tiers) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.tiers;
        return newErrors;
      });
    }
  };

  const handleBaseRewardLevelsChange = (levels: BaseRewardLevel[]) => {
    setFormData((prev) => ({ ...prev, baseRewardLevels: levels }));
  };

  const handleFileChange = async (fileName: string, file?: File | null) => {
    setWhitelistFileError(null);

    if (file) {
      setWhitelistFileUploading(true);
      try {
        const result = await UploadWhitelistFileUseCase(file);
        setFormData((prev) => ({
          ...prev,
          whitelistFile: fileName,
          customerList: result.preview.map((item) => ({
            customerId: item.customerId,
            phoneNumber: "",
            name: item.name,
            area: "",
            tier: item.tier,
          })),
          whitelistTemporaryId: result.temporaryFileId,
          whitelistTotalCount: result.totalCustomers,
        }));
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Failed to upload file";
        setWhitelistFileError(errorMessage);
        setFormData((prev) => ({
          ...prev,
          whitelistFile: "",
          customerList: [],
          whitelistTemporaryId: "",
          whitelistTotalCount: 0,
        }));
      } finally {
        setWhitelistFileUploading(false);
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        whitelistFile: fileName,
        customerList: [],
        whitelistTemporaryId: "",
        whitelistTotalCount: 0,
      }));
    }
  };

  const handleProductIncentiveFileChange = async (fileName: string, file?: File | null) => {
    setProductIncentiveFileError(null);

    if (file) {
      setProductFileUploading(true);
      try {
        const result = await UploadProductFileUseCase(file);
        setFormData((prev) => ({
          ...prev,
          productIncentiveFile: fileName,
          productIncentiveList: result.preview.map((item) => ({
            productId: item.productId,
            productName: item.productName,
            productDescription: "",
            pointType: "point",
            pointIncentive: item.pointIncentive,
          })),
          productTemporaryFileId: result.temporaryFileId,
          productTotalCount: result.totalProducts,
        }));
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Failed to upload file";
        setProductIncentiveFileError(errorMessage);
        setFormData((prev) => ({
          ...prev,
          productIncentiveFile: "",
          productIncentiveList: [],
          productTemporaryFileId: "",
          productTotalCount: 0,
        }));
      } finally {
        setProductFileUploading(false);
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        productIncentiveFile: fileName,
        productIncentiveList: [],
        productTemporaryFileId: "",
        productTotalCount: 0,
      }));
    }
  };

  const validateStep1 = (): boolean => {
    const newErrors: Partial<Record<keyof ProgramFormData, string>> = {};

    if (!formData.programName.trim()) {
      newErrors.programName = "Program name is required";
    }

    if (!formData.channel) {
      newErrors.channel = "Channel is required";
    }

    if (!formData.startDate) {
      newErrors.startDate = "Start date is required";
    }

    if (!formData.endDate) {
      newErrors.endDate = "End date is required";
    }

    if (
      formData.startDate &&
      formData.endDate &&
      formData.startDate > formData.endDate
    ) {
      newErrors.endDate = "End date must be after start date";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = (): boolean => {
    const newErrors: Partial<Record<keyof ProgramFormData, string>> = {};

    // Duwit channel validation
    if (formData.channel === "Duwit") {
      if (formData.baseRewardLevels.length === 0) {
        // No specific field error - validation handled in UI
      }

      if (!formData.maxBaseRewardPointsPerDay) {
        newErrors.maxBaseRewardPointsPerDay = "Maximum Base Reward Points Per Day is required";
      } else if (formData.maxBaseRewardPointsPerDay < 0) {
        newErrors.maxBaseRewardPointsPerDay = "Must be a positive number";
      }

      if (!formData.stampMinimumTransaction) {
        newErrors.stampMinimumTransaction = "Minimum Total Transaction is required";
      } else if (formData.stampMinimumTransaction < 0) {
        newErrors.stampMinimumTransaction = "Must be a positive number";
      }

      if (!formData.weeklyStampMinDays) {
        newErrors.weeklyStampMinDays = "Weekly Stamp Minimum Active Days is required";
      } else if (formData.weeklyStampMinDays < 0) {
        newErrors.weeklyStampMinDays = "Must be a positive number";
      }

      if (!formData.weeklyStampMultiplier) {
        newErrors.weeklyStampMultiplier = "Weekly Stamp Multiplier is required";
      } else if (formData.weeklyStampMultiplier < 0) {
        newErrors.weeklyStampMultiplier = "Must be a positive number";
      }

      if (!formData.monthlyStampMinDays) {
        newErrors.monthlyStampMinDays = "Monthly Stamp Minimum Active Days is required";
      } else if (formData.monthlyStampMinDays < 0) {
        newErrors.monthlyStampMinDays = "Must be a positive number";
      }

      if (!formData.monthlyStampMultiplier) {
        newErrors.monthlyStampMultiplier = "Monthly Stamp Multiplier is required";
      } else if (formData.monthlyStampMultiplier < 0) {
        newErrors.monthlyStampMultiplier = "Must be a positive number";
      }
    } else {
      // Non-Duwit channel validation
      if (!formData.basedOn) {
        newErrors.basedOn = "Based On is required";
      }

      if (!formData.minTransaction) {
        newErrors.minTransaction = "Minimum Transaction is required";
      } else if (parseInt(formData.minTransaction) <= 0) {
        newErrors.minTransaction = "Must be greater than 0";
      }

      if (!formData.maxTransaction) {
        newErrors.maxTransaction = "Maximum Transaction is required";
      } else if (parseInt(formData.maxTransaction) <= 0) {
        newErrors.maxTransaction = "Must be greater than 0";
      }

      if (
        formData.minTransaction &&
        formData.maxTransaction &&
        parseInt(formData.minTransaction) > 0 &&
        parseInt(formData.maxTransaction) > 0 &&
        parseInt(formData.minTransaction) > parseInt(formData.maxTransaction)
      ) {
        newErrors.maxTransaction = "Maximum must be greater than minimum";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = (): boolean => {
    const newErrors: Partial<Record<keyof ProgramFormData, string>> = {};

    if (!formData.area) {
      newErrors.area = "Area is required";
    }

    if (formData.tiers.length === 0) {
      newErrors.tiers = "At least one tier must be selected";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Convert date-only string (YYYY-MM-DD) to ISO 8601 datetime format.
   * Uses start of day (00:00:00) in UTC timezone.
   */
  const toISODateTime = (dateString: string): string => {
    if (!dateString) return "";
    return new Date(`${dateString}T00:00:00.000Z`).toISOString();
  };
  const buildWritePayload = (): ProgramWritePayload => {
    const isDuwit = formData.channel === "Duwit";

    const incentiveRule: ProgramWritePayload["incentiveRule"] = isDuwit
      ? {
          type: "tiered_stamp",
          maxBaseRewardPerDay: formData.maxBaseRewardPointsPerDay,
          baseRewards: formData.baseRewardLevels.map((level, index) => ({
            level: index + 1,
            minTransaction: level.minTransaction,
            maxTransaction: level.maxTransaction,
            pointsEarned: level.pointsEarned,
          })),
          stampMultiplier: {
            minTotalTransaction: formData.stampMinimumTransaction,
            weekly: {
              minActiveDays: formData.weeklyStampMinDays,
              multiplierPercentage: formData.weeklyStampMultiplier,
            },
            monthly: {
              minActiveDays: formData.monthlyStampMinDays,
              multiplierPercentage: formData.monthlyStampMultiplier,
            },
          },
        }
      : {
          type: formData.incentiveRule,
          basedOn: formData.basedOn,
          minimumTransaction: parseInt(formData.minTransaction, 10) || 0,
          maximumTransaction: parseInt(formData.maxTransaction, 10) || 0,
          ...(formData.productTemporaryFileId && {
            productTemporaryFileId: formData.productTemporaryFileId,
          }),
        };

    const payload: ProgramWritePayload = {
      program: {
        name: formData.channel,
        programName: formData.programName,
        startDate: toISODateTime(formData.startDate),
        endDate: toISODateTime(formData.endDate),
      },
      incentiveRule,
      customerSegment: {
        areas: [formData.area],
        tiers: [...formData.tiers],
        ...(formData.whitelistTemporaryId && {
          whitelistTemporaryId: formData.whitelistTemporaryId,
        }),
      },
    };

    return payload;
  };

  const handleSubmit = async () => {
    if (!validateStep3()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const payload = buildWritePayload();

      if (isEditMode && id) {
        await UpdateProgramUseCase(id, payload);
      } else {
        await CreateProgramUseCase(payload);
      }

      setShowSuccessModal(true);
    } catch (error) {
      console.error("Failed to save program:", error);
      setSubmitError(
        error instanceof Error
          ? error.message
          : "Failed to save program. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    if (currentStep === 1 && !validateStep1()) {
      return;
    }

    if (currentStep === 2 && !validateStep2()) {
      return;
    }

    if (currentStep < STEPS.length) {
      setCurrentStep((prev) => prev + 1);
    } else {
      void handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    navigate("/swarapoint/programs");
  };

  const handleCancel = () => {
    navigate("/swarapoint/programs");
  };

  const handleRetryLoadDetail = () => {
    if (id) {
      void loadDetail(id);
    }
  };

  const handleGoBack = () => {
    navigate("/swarapoint/programs");
  };

  // Loading state for edit mode
  if (isLoadingDetail) {
    return (
      <MainLayout
        title={isEditMode ? "Edit Program" : "Add New Program"}
        onBack={handleGoBack}
      >
        <div className="flex items-center justify-center py-12">
          <p className="text-slate-500">Loading program...</p>
        </div>
      </MainLayout>
    );
  }

  // Error state for edit mode
  if (isEditMode && loadDetailError) {
    return (
      <MainLayout title="Edit Program" onBack={handleGoBack}>
        <div className="flex flex-col items-center justify-center py-16 gap-6">
          <div className="flex flex-col items-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-red-500"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <h3 className="text-lg font-semibold text-slate-900">
              Unable to Load Program
            </h3>
            <p className="text-sm text-slate-500 text-center max-w-md">
              {loadDetailError}
            </p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" size="md" onClick={handleGoBack}>
              Go Back
            </Button>
            <Button variant="primary" size="md" onClick={handleRetryLoadDetail}>
              Retry
            </Button>
          </div>
        </div>
      </MainLayout>
    );
  }
  return (
    <MainLayout
      title={isEditMode ? "Edit Program" : "Add New Program"}
      onBack={handleGoBack}
    >
      <div className="flex flex-col gap-6">
        {/* Stepper */}
        <Stepper steps={STEPS} currentStep={currentStep} />

        {/* Form Content */}
        <div className="bg-white rounded-lg p-6 shadow-sm">
          {currentStep === 1 && (
            <EventTriggerStep
              formData={formData}
              errors={errors}
              onInputChange={handleInputChange}
            />
          )}

          {currentStep === 2 && (
            <IncentiveRuleStep
              formData={formData}
              errors={errors}
              onInputChange={handleInputChange}
              onProductIncentiveFileChange={handleProductIncentiveFileChange}
              productIncentiveFileError={productIncentiveFileError}
              isUploading={productFileUploading}
              onBaseRewardLevelsChange={handleBaseRewardLevelsChange}
            />
          )}

          {currentStep === 3 && (
            <CustomerSegmentStep
              formData={formData}
              errors={errors}
              onInputChange={handleInputChange}
              onTierChange={handleTierChange}
              onFileChange={handleFileChange}
              whitelistFileError={whitelistFileError}
              isUploading={whitelistFileUploading}
            />
          )}
        </div>

        {/* Submit Error */}
        {submitError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-600">{submitError}</p>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-end gap-3">
          <Button variant="outline" size="md" onClick={handleCancel}>
            Cancel
          </Button>
          {currentStep > 1 && (
            <Button variant="outline" size="md" onClick={handleBack}>
              Back
            </Button>
          )}
          <Button
            variant="primary"
            size="md"
            onClick={handleNext}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? "Saving..."
              : currentStep === STEPS.length
                ? isEditMode
                  ? "Update"
                  : "Submit"
                : "Next"}
          </Button>
        </div>
      </div>

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={handleSuccessModalClose}
        title={isEditMode ? "Program Updated" : "Program Created"}
        message={
          isEditMode
            ? "Your program has been successfully updated."
            : "Your program has been successfully created."
        }
      />
    </MainLayout>
  );
};

